import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
// hooks
import useAuth from 'src/hooks/useAuth';
// utils
import { getFriendlyName } from 'src/utils/automacoes.utils';
// components
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
// ui
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Card } from 'src/@/components/ui/card';
import { Switch } from 'src/@/components/ui/switch';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from 'src/@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
// table
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
// sections
import DialogCriarAutomacao from 'src/sections/@dashboard/automacoes/DialogCriarAutomacao';
import { DialogLogsAutomacao } from 'src/sections/@dashboard/automacoes/DialogLogsAutomacao';
// actions
import v2_actionEditarAutomacao from 'src/actions/v2/automacoes/v2_actionEditarAutomacao';
import v2_actionDeletarAutomacao from 'src/actions/v2/automacoes/v2_actionDeletarAutomacao';
import v2_actionAutomacoes from 'src/actions/v2/automacoes/v2_actionAutomacoes';
import {
    v2_actionListarGruposAutomacoes,
    v2_actionCriarGrupoAutomacoes,
    v2_actionEditarGrupoAutomacoes,
    v2_actionDeletarGrupoAutomacoes,
} from 'src/actions/v2/automacoes/v2_actionGruposAutomacoes';
import EmptyContentGhost from 'src/components/EmptyContentGhost';
import { fDateTime } from 'src/utils/formatTime';

// ----------------------------------------------------------------------

const CardAutomacao = ({ automacao, onSelect, onDelete, onChangeStatus, grupos, onMoveToGroup }) => (
    <Card
        onClick={onSelect}
        className="group relative flex flex-col gap-3 cursor-pointer p-4 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 dark:hover:bg-[#7C3AED]/10 hover:shadow-md transition-all duration-300"
    >
        {/* Header com nome e data */}
        <div className="flex items-start justify-between">
            <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] mb-0.5 group-hover:text-[#7C3AED] transition-colors">
                    {automacao?.name}
                </h4>
                <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] capitalize">{fDateTime(automacao?.create_at)}</span>
            </div>
            {/* Status e ações */}
            <div className="flex items-center gap-2">
                {automacao?.draft ? (
                    <span className="text-[10px] font-medium text-gray-500 dark:text-[#ABABAD] bg-gray-100 dark:bg-[#383838] border border-gray-200 dark:border-[#383838] px-2 py-1 rounded-full">
                        Rascunho
                    </span>
                ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                            checked={automacao?.status}
                            onCheckedChange={(checked) => onChangeStatus(automacao._id, checked)}
                            className="data-[state=checked]:bg-[#7C3AED]"
                        />
                    </div>
                )}
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDelete();
                    }}
                    className="h-7 w-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 text-gray-400 dark:text-[#ABABAD] transition-colors"
                >
                    <Iconify icon="material-symbols:delete-outline" className="text-[18px]" />
                </Button>
            </div>
        </div>

        {/* Fluxo da automação */}
        {!automacao?.draft && (
            <div className="flex items-center gap-2 text-xs">
                {/* Origem */}
                <div className="bg-violet-50/50 dark:bg-violet-500/10 text-[#7C3AED] border border-violet-100 dark:border-violet-500/20 rounded-lg px-3 py-1.5 font-medium min-w-[100px] text-center truncate">
                    {getFriendlyName(automacao?.params?.from?.action)}
                </div>
                {/* Seta */}
                <Iconify icon="mdi:arrow-right" className="text-gray-300 dark:text-[#ABABAD] flex-shrink-0" width={16} />
                {/* Destino */}
                <div className="bg-blue-50/50 dark:bg-blue-500/10 text-blue-600 border border-blue-100 dark:border-blue-500/20 rounded-lg px-3 py-1.5 font-medium min-w-[100px] text-center truncate">
                    {automacao?.params?.to?.length > 0 && getFriendlyName(automacao?.params?.to[0].value)}
                </div>
                {/* Contador de destinos adicionais */}
                {automacao?.params?.to?.length > 1 && (
                    <Badge variant="secondary" className="ml-1 bg-gray-100 dark:bg-[#383838] text-gray-600 dark:text-[#ABABAD] hover:bg-gray-200 dark:hover:bg-[#444] h-6">
                        +{automacao.params.to.length - 1}
                    </Badge>
                )}
            </div>
        )}

        {/* Seletor de grupo */}
        <div className="flex items-center justify-between pt-3 mt-1 border-t border-dashed border-gray-100 dark:border-[#383838]">
            <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-gray-500 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10">
                            <Iconify icon="mdi:folder-outline" className="mr-1.5" width={14} />
                            {automacao.group_id ? grupos.find((g) => g._id === automacao.group_id)?.name || 'Sem grupo' : 'Sem grupo'}
                            <Iconify icon="mdi:chevron-down" className="ml-1 opacity-50" width={12} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[180px]">
                        <DropdownMenuItem onClick={() => onMoveToGroup(automacao._id, null)} className="text-gray-500">
                            <Iconify icon="mdi:folder-open-outline" className="mr-2" width={16} />
                            Sem grupo
                        </DropdownMenuItem>
                        {grupos.map((grupo) => (
                            <DropdownMenuItem key={grupo._id} onClick={() => onMoveToGroup(automacao._id, grupo._id)}>
                                <Iconify icon="mdi:folder-outline" className="mr-2" width={16} />
                                {grupo.name}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    </Card>
);

const GrupoAutomacoes = ({
    grupo,
    automacoes,
    onSelectAutomacao,
    onDeleteAutomacao,
    onChangeStatus,
    onEditGrupo,
    onDeleteGrupo,
    grupos,
    onMoveToGroup,
}) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className="border border-gray-200 dark:border-[#383838] rounded-2xl mb-6 bg-white dark:bg-[#222529] shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
            <div
                className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-[#1A1D21]/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1A1D21] transition-colors border-b border-gray-50 dark:border-[#383838]"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3">
                    <div className={`p-1 rounded-md transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                        <Iconify icon="mdi:chevron-right" className="text-gray-400 dark:text-[#ABABAD]" width={20} />
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-violet-100 dark:bg-violet-500/10 text-[#7C3AED] rounded-lg">
                            <Iconify icon="mdi:folder" width={18} />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-[#D1D2D3]">{grupo.name}</h3>
                        <Badge variant="secondary" className="ml-2 bg-gray-100 dark:bg-[#383838] text-gray-500 dark:text-[#ABABAD] font-normal">
                            {automacoes.length}
                        </Badge>
                    </div>
                </div>
                <div className="flex items-center">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-lg text-gray-400 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3] hover:bg-gray-100 dark:hover:bg-[#383838]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Iconify icon="mdi:dots-vertical" width={16} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditGrupo(grupo)}>
                                <Iconify icon="mdi:pencil-outline" className="mr-2" width={16} />
                                Editar grupo
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDeleteGrupo(grupo)} className="text-red-600 focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-500/10">
                                <Iconify icon="mdi:delete-outline" className="mr-2" width={16} />
                                Excluir grupo
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            {isExpanded && (
                <div className="p-4 bg-white dark:bg-[#222529]">
                    {automacoes.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {automacoes.map((automacao, index) => (
                                <CardAutomacao
                                    key={index}
                                    automacao={automacao}
                                    onSelect={() => onSelectAutomacao(automacao)}
                                    onDelete={() => onDeleteAutomacao(automacao)}
                                    onChangeStatus={onChangeStatus}
                                    grupos={grupos}
                                    onMoveToGroup={onMoveToGroup}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="py-8 text-center">
                            <p className="text-sm text-gray-400 dark:text-[#ABABAD] italic">Nenhuma automação neste grupo</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const DialogCriarGrupo = ({ open, onClose, onConfirm, grupoEditando = null }) => {
    const [nomeGrupo, setNomeGrupo] = useState(grupoEditando?.name || '');

    useEffect(() => {
        if (grupoEditando) {
            setNomeGrupo(grupoEditando.name);
        } else {
            setNomeGrupo('');
        }
    }, [grupoEditando]);

    const handleSubmit = () => {
        if (nomeGrupo.trim()) {
            onConfirm(nomeGrupo.trim(), grupoEditando);
            setNomeGrupo('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{grupoEditando ? 'Editar Grupo' : 'Criar Novo Grupo'}</DialogTitle>
                    <DialogDescription>
                        {grupoEditando ? 'Edite o nome do grupo de automações' : 'Crie um novo grupo para organizar suas automações'}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="nome-grupo">Nome do Grupo</Label>
                        <Input
                            id="nome-grupo"
                            value={nomeGrupo}
                            onChange={(e) => setNomeGrupo(e.target.value)}
                            placeholder="Ex: Automações de Vendas"
                            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} disabled={!nomeGrupo.trim()}>
                        {grupoEditando ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function Automacoes() {
    const { cliente, user } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [automacoes, setAutomacoes] = useState([]);
    const [filteredAutomacoes, setFilteredAutomacoes] = useState([]);
    const [statusFilter, setStatusFilter] = useState('todos');
    const [sortOrder, setSortOrder] = useState('recent');
    const [searchQuery, setSearchQuery] = useState('');
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [idAutomacao, setIdAutomacao] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Estados para grupos
    const [grupos, setGrupos] = useState([]);
    const [openDialogGrupo, setOpenDialogGrupo] = useState(false);
    const [grupoEditando, setGrupoEditando] = useState(null);
    const [viewMode, setViewMode] = useState('grupos'); // "grupos" ou "lista"
    const [loadingGrupos, setLoadingGrupos] = useState(false);

    // Carregar grupos da API
    const carregarGrupos = async () => {
        try {
            setLoadingGrupos(true);
            const response = await v2_actionListarGruposAutomacoes(cliente._id);
            if (!response.data.error) {
                setGrupos(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao carregar grupos:', error);
            enqueueSnackbar('Erro ao carregar grupos de automações', { variant: 'error' });
        } finally {
            setLoadingGrupos(false);
        }
    };

    async function getAutomacoes() {
        try {
            const response = await v2_actionAutomacoes(cliente._id);
            const automacoesDaAPI = response.data.data;
            setAutomacoes(automacoesDaAPI);
            setFilteredAutomacoes(automacoesDaAPI);
        } catch (err) {
            console.log(err);
        }
    }

    async function deleteAutomacao(id) {
        try {
            setLoadingDelete(true);
            await v2_actionDeletarAutomacao(id);
            await getAutomacoes();
            setLoadingDelete(false);
        } catch (err) {
            setLoadingDelete(false);
            console.log(err);
        }
    }

    const handleEditAutomation = async (id, status) => {
        let data = { scope: { status: status } };
        try {
            await v2_actionEditarAutomacao(id, data);
            enqueueSnackbar('Automação editada com sucesso!', { variant: 'success' });
            getAutomacoes();
        } catch (error) {
            console.error('Erro ao editar automação:', error);
            enqueueSnackbar('Erro ao salvar automação', { variant: 'error' });
            return;
        }
    };

    // Funções para gerenciar grupos via API
    const criarGrupo = async (nome, grupoEditando = null) => {
        try {
            if (grupoEditando) {
                // Editar grupo existente
                const response = await v2_actionEditarGrupoAutomacoes(grupoEditando._id, { name: name });
                if (!response.data.error) {
                    enqueueSnackbar('Grupo editado com sucesso!', { variant: 'success' });
                    await carregarGrupos(); // Recarregar grupos da API
                }
            } else {
                // Criar novo grupo
                const response = await v2_actionCriarGrupoAutomacoes(cliente._id, { name: nome });
                if (!response.data.error) {
                    enqueueSnackbar('Grupo criado com sucesso!', { variant: 'success' });
                    await carregarGrupos(); // Recarregar grupos da API
                }
            }
        } catch (error) {
            console.error('Erro ao gerenciar grupo:', error);
            enqueueSnackbar('Erro ao gerenciar grupo', { variant: 'error' });
        }
    };

    const deletarGrupo = async (grupo) => {
        try {
            const response = await v2_actionDeletarGrupoAutomacoes(grupo._id);
            if (!response.data.error) {
                enqueueSnackbar('Grupo excluído com sucesso!', { variant: 'success' });
                await carregarGrupos(); // Recarregar grupos da API
            }
        } catch (error) {
            console.error('Erro ao deletar grupo:', error);
            enqueueSnackbar('Erro ao excluir grupo', { variant: 'error' });
        }
    };

    const editarGrupo = (grupo) => {
        setGrupoEditando(grupo);
        setOpenDialogGrupo(true);
    };

    // Função para mover automação para um grupo ou remover do grupo
    const handleMoveToGroup = async (automacaoId, grupoId) => {
        try {
            // Atualizar a automação via API
            const data = { scope: { group_id: grupoId || null } };
            await v2_actionEditarAutomacao(automacaoId, data);
            // Atualizar localmente para uma resposta mais rápida
            const automacoesAtualizadas = automacoes.map((automacao) =>
                automacao._id === automacaoId ? { ...automacao, group_id: grupoId || null } : automacao
            );
            setAutomacoes(automacoesAtualizadas);
            setFilteredAutomacoes(automacoesAtualizadas);
            enqueueSnackbar('Automação movida com sucesso!', { variant: 'success' });
        } catch (error) {
            console.error('Erro ao mover automação:', error);
            enqueueSnackbar('Erro ao mover automação', { variant: 'error' });
        }
    };

    // Organizar automações por grupos
    const organizarAutomacoesPorGrupos = () => {
        const automacoesComGrupo = filteredAutomacoes.filter((automacao) => automacao.group_id);
        const automacoesSemGrupo = filteredAutomacoes.filter((automacao) => !automacao.group_id);
        const gruposComAutomacoes = grupos.map((grupo) => ({
            ...grupo,
            automacoes: automacoesComGrupo.filter((automacao) => automacao.group_id === grupo._id),
        }));
        return { gruposComAutomacoes, automacoesSemGrupo };
    };

    useEffect(() => {
        getAutomacoes();
        carregarGrupos();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [statusFilter, sortOrder, searchQuery]);

    const applyFilters = () => {
        let filtered = [...automacoes];
        // Filter by search
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter((automacao) => automacao.name?.toLowerCase().includes(query));
        }
        // Filter by status
        if (statusFilter !== 'todos') {
            const isActive = statusFilter === 'active';
            filtered = filtered.filter((automacao) => automacao.status === isActive);
        }
        // Sort alphabetically or by recent
        if (sortOrder === 'alphabetical') {
            filtered.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortOrder === 'recent') {
            filtered.sort((a, b) => new Date(b.updatedAt || b.create_at) - new Date(a.updatedAt || a.create_at));
        }
        setFilteredAutomacoes(filtered);
    };

    const { gruposComAutomacoes, automacoesSemGrupo } = organizarAutomacoesPorGrupos();

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => {
                    setOpenModalConfirmacao(false);
                    setIdAutomacao(null);
                }}
                onConfirm={async () => {
                    try {
                        await deleteAutomacao(idAutomacao);
                        setOpenModalConfirmacao(false);
                        setIdAutomacao(null);
                    } catch (err) {
                        enqueueSnackbar('Erro ao excluir automação', { variant: 'error' });
                    }
                }}
                loadingFinalizar={false}
                mensagem={'Deseja mesmo excluir?'}
            />

            <DialogCriarGrupo
                open={openDialogGrupo}
                onClose={() => {
                    setOpenDialogGrupo(false);
                    setGrupoEditando(null);
                }}
                onConfirm={criarGrupo}
                grupoEditando={grupoEditando}
            />

            {/* Sticky Header */}
            <div className="sticky top-0 z-20 bg-white/80 dark:bg-[#222529] border-b border-gray-100/50 dark:border-[#383838] px-6 py-4 shadow-sm backdrop-blur-md">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Title */}
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-[#7C3AED]/10 dark:bg-[#7C3AED]/20 rounded-lg">
                                <Iconify icon="mdi:robot" className="text-[#7C3AED]" width={20} />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">Automações</h3>
                                <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Gerencie seus fluxos automáticos</p>
                            </div>
                        </div>

                        <div className="h-8 w-px bg-gray-200 dark:bg-[#383838] hidden md:block" />

                        {/* Search */}
                        <div className="relative">
                            <Iconify
                                icon="eva:search-outline"
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#ABABAD] pointer-events-none"
                                width={16}
                            />
                            <input
                                type="text"
                                placeholder="Buscar automação..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-[200px] bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-lg pl-9 pr-3 py-1.5 text-xs text-gray-700 dark:text-[#D1D2D3] placeholder:text-gray-400 dark:placeholder:text-[#ABABAD] hover:border-[#7C3AED]/50 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] transition-all"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-[#ABABAD]">Status:</span>
                            <div className="relative">
                                <select
                                    className="appearance-none bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-lg pl-3 pr-8 py-1.5 text-xs font-medium text-gray-700 dark:text-[#D1D2D3] hover:border-[#7C3AED]/50 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 transition-all cursor-pointer"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="todos">Todos</option>
                                    <option value="active">Ativo</option>
                                    <option value="inactive">Inativo</option>
                                </select>
                                <Iconify
                                    icon="mdi:chevron-down"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#ABABAD] pointer-events-none"
                                    width={14}
                                />
                            </div>
                        </div>

                        {/* Sort Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-[#ABABAD]">Ordenar:</span>
                            <div className="relative">
                                <select
                                    className="appearance-none bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-lg pl-3 pr-8 py-1.5 text-xs font-medium text-gray-700 dark:text-[#D1D2D3] hover:border-[#7C3AED]/50 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 transition-all cursor-pointer"
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                >
                                    <option value="recent">Mais recente</option>
                                    <option value="alphabetical">Ordem alfabética</option>
                                </select>
                                <Iconify
                                    icon="mdi:chevron-down"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#ABABAD] pointer-events-none"
                                    width={14}
                                />
                            </div>
                        </div>

                        {/* View Mode */}
                        <div className="bg-gray-100/50 dark:bg-[#1A1D21]/50 p-1 rounded-lg border border-gray-200/50 dark:border-[#383838] flex items-center">
                            <button
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'grupos' ? 'bg-white dark:bg-[#383838] text-[#7C3AED] shadow-sm' : 'text-gray-400 dark:text-[#ABABAD] hover:text-gray-600 dark:hover:text-[#D1D2D3]'
                                    }`}
                                onClick={() => setViewMode('grupos')}
                                title="Visualização por Grupos"
                            >
                                <Iconify icon="mdi:folder-table-outline" width={16} />
                            </button>
                            <button
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'lista' ? 'bg-white dark:bg-[#383838] text-[#7C3AED] shadow-sm' : 'text-gray-400 dark:text-[#ABABAD] hover:text-gray-600 dark:hover:text-[#D1D2D3]'
                                    }`}
                                onClick={() => setViewMode('lista')}
                                title="Visualização em Lista"
                            >
                                <Iconify icon="mdi:format-list-bulleted" width={16} />
                            </button>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            className="h-9 text-xs border-dashed border-gray-300 dark:border-[#383838] text-gray-600 dark:text-[#ABABAD] hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-all"
                            onClick={() => {
                                setGrupoEditando(null);
                                setOpenDialogGrupo(true);
                            }}
                            disabled={loadingGrupos}
                        >
                            <Iconify icon="mdi:folder-plus-outline" className="mr-2" width={16} />
                            Novo Grupo
                        </Button>
                        <div className="mx-1">
                            <DialogLogsAutomacao user={user} />
                        </div>
                        <DialogCriarAutomacao
                            user={user}
                            onCallback={(data) => {
                                setAutomacoes([...automacoes, data]);
                                navigate(`/dashboard/automacoes/${data._id}`);
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 scroll-smooth">
                {filteredAutomacoes.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center p-8">
                        <EmptyContentGhost
                            title="Nenhuma automação encontrada"
                            description="Crie uma nova automação para começar a enviar mensagens automaticamente."
                        />
                        <Button
                            variant="default"
                            className="mt-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                            onClick={() => {
                                document.querySelector('[data-dialog-criar-automacao]')?.click();
                            }}
                        >
                            <Iconify icon="mdi:plus" className="mr-2" />
                            Criar Automação
                        </Button>
                    </div>
                )}

                {/* Automations Display */}
                {viewMode === 'grupos' ? (
                    <div className="max-w-[95vw] mx-auto space-y-6 pb-12">
                        {/* Grupos com automações */}
                        {gruposComAutomacoes.map((grupo) => (
                            <GrupoAutomacoes
                                key={grupo._id}
                                grupo={grupo}
                                automacoes={grupo.automacoes}
                                onSelectAutomacao={(automacao) => navigate(`/dashboard/automacoes/workflow/${automacao._id}`)}
                                onDeleteAutomacao={(automacao) => {
                                    setIdAutomacao(automacao._id);
                                    setOpenModalConfirmacao(true);
                                }}
                                onChangeStatus={(id, status) => handleEditAutomation(id, status)}
                                onEditGrupo={editarGrupo}
                                onDeleteGrupo={deletarGrupo}
                                grupos={grupos}
                                onMoveToGroup={handleMoveToGroup}
                            />
                        ))}

                        {/* Automações sem grupo */}
                        {automacoesSemGrupo.length > 0 && (
                            <div className="border border-gray-200 dark:border-[#383838] rounded-2xl bg-white dark:bg-[#222529] shadow-sm overflow-hidden">
                                <div className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-[#1A1D21]/50 border-b border-gray-50 dark:border-[#383838]">
                                    <div className="flex items-center gap-3">
                                        <div className="p-1 px-2">
                                            {/* Spacer to align with chevron */}
                                            <Iconify icon="mdi:folder-hidden" className="text-gray-300 dark:text-[#ABABAD]" width={18} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-sm font-semibold text-gray-600 dark:text-[#ABABAD]">Sem Grupo</h3>
                                            <Badge variant="secondary" className="bg-gray-100 dark:bg-[#383838] text-gray-500 dark:text-[#ABABAD] font-normal">
                                                {automacoesSemGrupo.length}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 bg-white dark:bg-[#222529]">
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {automacoesSemGrupo.map((automacao, index) => (
                                            <CardAutomacao
                                                key={index}
                                                automacao={automacao}
                                                onSelect={() => navigate(`/dashboard/automacoes/workflow/${automacao._id}`)}
                                                onDelete={() => {
                                                    setIdAutomacao(automacao._id);
                                                    setOpenModalConfirmacao(true);
                                                }}
                                                onChangeStatus={(id, status) => handleEditAutomation(id, status)}
                                                grupos={grupos}
                                                onMoveToGroup={handleMoveToGroup}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Visualização em Lista (tabela) */
                    <div className="max-w-[95vw]">
                        <GenericTable
                            headLabels={[
                                { id: 'nome', label: 'Nome', className: 'flex-1 min-w-[220px] pr-4' },
                                { id: 'fluxo', label: 'Fluxo', className: 'flex-1 min-w-[260px] pr-4' },
                                { id: 'grupo', label: 'Grupo', className: 'w-[160px] pr-4' },
                                { id: 'data', label: 'Criado em', className: 'w-[150px] pr-4' },
                                { id: 'status', label: 'Status', className: 'w-[100px] pr-4' },
                                { id: 'acoes', label: '', className: 'w-[80px] text-right' },
                            ]}
                        >
                            {filteredAutomacoes.map((automacao) => (
                                <div
                                    key={automacao._id}
                                    className={`${GENERIC_TABLE_DESIGN.row.container} cursor-pointer`}
                                    onClick={() => navigate(`/dashboard/automacoes/${automacao._id}`)}
                                >
                                    {/* Nome */}
                                    <div className="flex-1 min-w-[220px] pr-4 flex items-center">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] truncate group-hover:text-[#7C3AED] transition-colors">
                                                {automacao?.name}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Fluxo */}
                                    <div className="flex-1 min-w-[260px] pr-4 flex items-center gap-2">
                                        {!automacao?.draft ? (
                                            <>
                                                <span className="bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED] border border-violet-100 dark:border-violet-500/20 rounded-lg px-2.5 py-1 text-xs font-medium truncate max-w-[120px]">
                                                    {getFriendlyName(automacao?.params?.from?.action)}
                                                </span>
                                                <Iconify icon="mdi:arrow-right" className="text-gray-300 dark:text-[#ABABAD] flex-shrink-0" width={14} />
                                                <span className="bg-blue-50 dark:bg-blue-500/10 text-blue-600 border border-blue-100 dark:border-blue-500/20 rounded-lg px-2.5 py-1 text-xs font-medium truncate max-w-[120px]">
                                                    {automacao?.params?.to?.length > 0 && getFriendlyName(automacao?.params?.to[0].value)}
                                                </span>
                                                {automacao?.params?.to?.length > 1 && (
                                                    <Badge variant="secondary" className="bg-gray-100 dark:bg-[#383838] text-gray-600 dark:text-[#ABABAD] h-5 text-[10px]">
                                                        +{automacao.params.to.length - 1}
                                                    </Badge>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-[#ABABAD] italic">Rascunho — sem fluxo definido</span>
                                        )}
                                    </div>

                                    {/* Grupo */}
                                    <div className="w-[160px] pr-4" onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-gray-500 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10">
                                                    <Iconify icon="mdi:folder-outline" className="mr-1.5" width={14} />
                                                    <span className="truncate max-w-[100px]">
                                                        {automacao.group_id ? grupos.find((g) => g._id === automacao.group_id)?.name || 'Sem grupo' : 'Sem grupo'}
                                                    </span>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start" className="w-[180px]">
                                                <DropdownMenuItem onClick={() => handleMoveToGroup(automacao._id, null)} className="text-gray-500">
                                                    <Iconify icon="mdi:folder-open-outline" className="mr-2" width={16} />
                                                    Sem grupo
                                                </DropdownMenuItem>
                                                {grupos.map((grupo) => (
                                                    <DropdownMenuItem key={grupo._id} onClick={() => handleMoveToGroup(automacao._id, grupo._id)}>
                                                        <Iconify icon="mdi:folder-outline" className="mr-2" width={16} />
                                                        {grupo.name}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>

                                    {/* Data */}
                                    <div className="w-[150px] pr-4">
                                        <span className="text-xs text-gray-500 dark:text-[#ABABAD] capitalize">{fDateTime(automacao?.create_at)}</span>
                                    </div>

                                    {/* Status */}
                                    <div className="w-[100px] pr-4 flex items-center" onClick={(e) => e.stopPropagation()}>
                                        {automacao?.draft ? (
                                            <Badge variant="outline" className="text-[10px] font-medium text-gray-500 dark:text-[#ABABAD] bg-gray-50 dark:bg-[#383838] border-gray-200 dark:border-[#383838]">
                                                Rascunho
                                            </Badge>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={automacao?.status}
                                                    onCheckedChange={(checked) => handleEditAutomation(automacao._id, checked)}
                                                    className="data-[state=checked]:bg-[#7C3AED]"
                                                />
                                                <span className={`text-[10px] font-medium ${automacao?.status ? 'text-green-600' : 'text-gray-400'}`}>
                                                    {automacao?.status ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Ações */}
                                    <div className="w-[80px] flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            setIdAutomacao(automacao._id);
                                                            setOpenModalConfirmacao(true);
                                                        }}
                                                        className="h-8 w-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 text-gray-400 dark:text-[#ABABAD] transition-colors"
                                                    >
                                                        <Iconify icon="material-symbols:delete-outline" className="text-[18px]" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="left">
                                                    <p>Excluir automação</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </div>
                            ))}
                        </GenericTable>
                    </div>
                )}
            </div>
        </div>
    );
}